import React, { useState } from "react";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import CanvasAiComposer from "./CanvasAiComposer";
import CanvasAiKeyForm from "./CanvasAiKeyForm";
import CanvasAiMessages from "./CanvasAiMessages";
import CanvasAiModelSelect from "./CanvasAiModelSelect";
import "./CanvasAiChat.css";
import "./CanvasAiPanel.css";

const IconButton = ({ Icon, label, onClick }) => (
  <button
    aria-label={label}
    className="canvas-ai-icon-button"
    onClick={onClick}
    title={label}
    type="button"
  >
    <Icon fontSize="small" />
  </button>
);

const CanvasAiPanel = ({ assistant, onClose }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const isCredentialView = !assistant.hasCredential || isSettingsOpen;
  const handleReturnToChat = () => setIsSettingsOpen(false);
  const panelClassName = [
    "canvas-ai-panel",
    isMinimized ? "canvas-ai-panel--minimized" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <aside aria-label="AI diagram assistant" className={panelClassName}>
      <header className="canvas-ai-header">
        <div className="canvas-ai-title-row">
          <AutoAwesomeOutlinedIcon fontSize="small" />
          <h2>AI diagrams</h2>
          {assistant.status === "streaming" && (
            <span className="canvas-ai-status-dot" title="Generating" />
          )}
        </div>
        <div className="canvas-ai-header-actions">
          {!isMinimized && assistant.hasCredential && (
            <>
              <IconButton
                Icon={isSettingsOpen ? ChatOutlinedIcon : AddCommentOutlinedIcon}
                label={isSettingsOpen ? "Back to current chat" : "New AI chat"}
                onClick={isSettingsOpen ? handleReturnToChat : assistant.startNewChat}
              />
              <IconButton
                Icon={SettingsOutlinedIcon}
                label="OpenRouter settings"
                onClick={() => setIsSettingsOpen(true)}
              />
            </>
          )}
          <IconButton
            Icon={
              isMinimized
                ? KeyboardArrowUpOutlinedIcon
                : KeyboardArrowDownOutlinedIcon
            }
            label={isMinimized ? "Expand AI assistant" : "Minimize AI assistant"}
            onClick={() => setIsMinimized((previous) => !previous)}
          />
          <IconButton Icon={CloseOutlinedIcon} label="Close AI assistant" onClick={onClose} />
        </div>
      </header>
      {!isMinimized && (
        <div className="canvas-ai-content">
          {assistant.error && (
            <div className="canvas-ai-error" role="alert">
              <span>{assistant.error}</span>
              <button onClick={assistant.clearError} type="button">
                Dismiss
              </button>
            </div>
          )}
          {isCredentialView ? (
            <CanvasAiKeyForm
              assistant={assistant}
              onCancel={
                assistant.hasCredential ? handleReturnToChat : undefined
              }
              onConnected={handleReturnToChat}
            />
          ) : (
            <>
              <CanvasAiModelSelect
                isLoading={
                  assistant.isModelsLoading || assistant.status === "streaming"
                }
                modelId={assistant.modelId}
                models={assistant.models}
                onChange={assistant.selectModel}
              />
              <CanvasAiMessages
                messages={assistant.messages}
                status={assistant.status}
              />
              {assistant.canUndoLastChange && (
                <button
                  className="canvas-ai-undo-button"
                  onClick={assistant.undoLastChange}
                  type="button"
                >
                  <UndoOutlinedIcon fontSize="small" />
                  Undo AI canvas update
                </button>
              )}
              <CanvasAiComposer
                canSend={Boolean(assistant.modelId)}
                onSend={assistant.sendMessage}
                onStop={assistant.stopGeneration}
                status={assistant.status}
              />
            </>
          )}
        </div>
      )}
    </aside>
  );
};

export default CanvasAiPanel;
